/*
 * Report Field Translations
 *
 * This contains translations for Flexmonster field captions
 */
import { defineMessages } from 'react-intl'

export const fieldMessages = defineMessages({
  // Basic fields
  company: {
    id: 'app.report.fields.company',
    defaultMessage: 'Company',
  },
  datasource: {
    id: 'app.report.fields.datasource',
    defaultMessage: 'Datasource',
  },
  amount: {
    id: 'app.report.fields.amount',
    defaultMessage: 'Amount',
  },
  kpl: {
    id: 'app.report.fields.kpl',
    defaultMessage: 'Quantity',
  },
  accountId: {
    id: 'app.report.fields.accountId',
    defaultMessage: 'accountId',
  },
  accountingPeriod: {
    id: 'app.report.fields.accountingPeriod',
    defaultMessage: 'Accounting Period',
  },
  dataSourceOrder: {
    id: 'app.report.fields.dataSourceOrder',
    defaultMessage: 'dataSourceOrder',
  },
  date: {
    id: 'app.report.fields.date',
    defaultMessage: 'Date',
  },
  dates: {
    id: 'app.report.fields.dates',
    defaultMessage: 'Dates',
  },
  month: {
    id: 'app.report.fields.month',
    defaultMessage: 'Month',
  },
  year: {
    id: 'app.report.fields.year',
    defaultMessage: 'Year',
  },
  quarter: {
    id: 'app.report.fields.quarter',
    defaultMessage: 'Quarter',
  },
  dimension: {
    id: 'app.report.fields.dimension',
    defaultMessage: 'Dimension',
  },
  dimensionValue: {
    id: 'app.report.fields.dimensionValue',
    defaultMessage: 'Dimension Value',
  },
  recordId: {
    id: 'app.report.fields.recordId',
    defaultMessage: 'recordId',
  },
  isBudgetData: {
    id: 'app.report.fields.isBudgetData',
    defaultMessage: 'isBudgetData',
  },
  percentageTargetAmount: {
    id: 'app.report.fields.percentageTargetAmount',
    defaultMessage: '% Target',
  },
  isCumulativeData: {
    id: 'app.report.fields.isCumulativeData',
    defaultMessage: 'isCumulativeData',
  },
  isEdited: {
    id: 'app.report.fields.isEdited',
    defaultMessage: 'isEdited',
  },
  isFormulaRowData: {
    id: 'app.report.fields.isFormulaRowData',
    defaultMessage: 'isFormulaRowData',
  },
  operativeItemIds: {
    id: 'app.report.fields.operativeItemIds',
    defaultMessage: 'operativeItemIds',
  },
  realBalanceIds: {
    id: 'app.report.fields.realBalanceIds',
    defaultMessage: 'realBalanceIds',
  },
  sourceCompany: {
    id: 'app.report.fields.sourceCompany',
    defaultMessage: 'sourceCompany',
  },
  reportBalanceIds: {
    id: 'app.report.fields.reportBalanceIds',
    defaultMessage: 'reportBalanceIds',
  },
  rows: {
    id: 'app.report.fields.rows',
    defaultMessage: 'Rows',
  },
  // Also handle capitalized variant that may come from data
  Rows: {
    id: 'app.report.fields.rows',
    defaultMessage: 'Rows',
  },
})

/**
 * Folder translations for Flexmonster field grouping
 */
export const folderMessages = defineMessages({
  'Data Property': {
    id: 'app.report.folders.dataProperty',
    defaultMessage: 'Data Property',
  },
  Dates: {
    id: 'app.report.folders.dates',
    defaultMessage: 'Dates',
  },
  Rows: {
    id: 'app.report.folders.rows',
    defaultMessage: 'Rows',
  },
})

/**
 * Hierarchy translations for Flexmonster field hierarchies
 */
export const hierarchyMessages = defineMessages({
  rows: {
    id: 'app.report.hierarchies.rows',
    defaultMessage: 'Rows',
  },
  Rows: {
    id: 'app.report.hierarchies.rows',
    defaultMessage: 'Rows',
  },
  columns: {
    id: 'app.report.hierarchies.columns',
    defaultMessage: 'Columns',
  },
  Columns: {
    id: 'app.report.hierarchies.columns',
    defaultMessage: 'Columns',
  },
  measures: {
    id: 'app.report.hierarchies.measures',
    defaultMessage: 'Measures',
  },
  Measures: {
    id: 'app.report.hierarchies.measures',
    defaultMessage: 'Measures',
  },
})

/**
 * Get translated caption for a field
 * @param {string} fieldName - The field name (uniqueName)
 * @param {function|object} formatMessageOrTranslations - Either formatMessage function or pre-calculated translations map
 * @returns {string} Translated caption or original fieldName as fallback
 */
export const getFieldCaption = (fieldName, formatMessageOrTranslations) => {
  if (!formatMessageOrTranslations) return fieldName

  // If it's a pre-calculated translations map object with fields/folders structure
  if (
    typeof formatMessageOrTranslations === 'object' &&
    formatMessageOrTranslations.fields
  ) {
    return formatMessageOrTranslations.fields[fieldName] || fieldName
  }

  // If it's a simple translations map (legacy support)
  if (
    typeof formatMessageOrTranslations === 'object' &&
    !formatMessageOrTranslations.fields
  ) {
    return formatMessageOrTranslations[fieldName] || fieldName
  }

  // Legacy: formatMessage function support
  const formatMessage = formatMessageOrTranslations
  // Check if translation exists
  if (fieldMessages[fieldName]) {
    return formatMessage(fieldMessages[fieldName])
  }

  // Handle dynamic datasource fields (e.g., "datasource 1", "datasource 2")
  const datasourceMatch = fieldName.match(/^datasource (\d+)$/)
  if (datasourceMatch) {
    const num = datasourceMatch[1]
    return `${formatMessage(fieldMessages.datasource)} ${num}`
  }

  // Fallback: return original field name
  return fieldName
}

/**
 * Pre-calculate all field translations for a given locale
 * Returns a stable object that can be used instead of formatMessage function
 * @param {function} formatMessage - The react-intl formatMessage function
 * @returns {object} Object with fields, folders and hierarchies maps
 */
export const createFieldTranslationsMap = (formatMessage) => {
  if (!formatMessage) return { fields: {}, folders: {}, hierarchies: {} }

  const fields = {}
  Object.keys(fieldMessages).forEach((fieldName) => {
    fields[fieldName] = formatMessage(fieldMessages[fieldName])
  })

  const folders = {}
  Object.keys(folderMessages).forEach((folderName) => {
    folders[folderName] = formatMessage(folderMessages[folderName])
  })

  const hierarchies = {}
  Object.keys(hierarchyMessages).forEach((hierarchyName) => {
    hierarchies[hierarchyName] = formatMessage(hierarchyMessages[hierarchyName])
  })

  return { fields, folders, hierarchies }
}

/**
 * Get translated caption for a field from translations map
 * @param {string} fieldName - The field name
 * @param {object} translationsMap - The translations map from createFieldTranslationsMap
 * @returns {string} Translated caption or original fieldName as fallback
 */
export const getFieldCaptionFromMap = (fieldName, translationsMap) => {
  if (!translationsMap?.fields) return fieldName
  return translationsMap.fields[fieldName] || fieldName
}

/**
 * Get translated folder name from translations map
 * @param {string} folderName - The folder name
 * @param {object} translationsMap - The translations map from createFieldTranslationsMap
 * @returns {string} Translated folder name or original folderName as fallback
 */
export const getFolderNameFromMap = (folderName, translationsMap) => {
  if (!translationsMap?.folders || !folderName) return folderName
  return translationsMap.folders[folderName] || folderName
}
/**
 * Get translated hierarchy name from translations map
 * @param {string} hierarchyName - The hierarchy name
 * @param {object} translationsMap - The translations map from createFieldTranslationsMap
 * @returns {string} Translated hierarchy name or original hierarchyName as fallback
 */
export const getHierarchyNameFromMap = (hierarchyName, translationsMap) => {
  if (!translationsMap?.hierarchies || !hierarchyName) return hierarchyName
  return translationsMap.hierarchies[hierarchyName] || hierarchyName
}

/**
 * Get translated folder name
 * @param {string} folderName - The folder name
 * @param {function} formatMessage - The react-intl formatMessage function
 * @returns {string} Translated folder name or original folderName as fallback
 */
export const getFolderName = (folderName, formatMessage) => {
  if (!formatMessage || !folderName) return folderName

  // Check if translation exists
  if (folderMessages[folderName]) {
    return formatMessage(folderMessages[folderName])
  }

  // Fallback: return original folder name
  return folderName
}

/**
 * Get translated hierarchy name
 * @param {string} hierarchyName - The hierarchy name
 * @param {function} formatMessage - The react-intl formatMessage function
 * @returns {string} Translated hierarchy name or original hierarchyName as fallback
 */
export const getHierarchyName = (hierarchyName, formatMessage) => {
  if (!formatMessage || !hierarchyName) return hierarchyName

  // Check if translation exists
  if (hierarchyMessages[hierarchyName]) {
    return formatMessage(hierarchyMessages[hierarchyName])
  }

  // Fallback: return original hierarchy name
  return hierarchyName
}
